import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/LanguageProvider";
import { buildWomanImageAlt } from "../../utils/imageAlt";
import { getWomanLargeImageUrl } from "../../utils/imageUrl";
import { getWomanCategoryPath, getWomanPlacePath } from "../../utils/slug";
import "./WomanProfileDetails.sass";

const LOCALES = { rom: "ro-RO", rus: "ru-RU", eng: "en-US" };

/** "Label | https://..." or "https://..." per line → [{ url, label }] (mirror of parseSourcesText() in backend/core.php). */
export function parseSources(text) {
    const seen = new Set();

    return String(text || "")
        .split(/\r\n|\r|\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const parts = line.split("|");
            const url = parts.length > 1 ? parts.pop().trim() : line;
            let label = parts.length > 0 && parts[0] !== line ? parts.join("|").trim() : "";

            if (!/^https?:\/\/[^\s<>"']+$/i.test(url) || seen.has(url)) {
                return null;
            }

            seen.add(url);

            if (!label) {
                try {
                    label = new URL(url).hostname.replace(/^www\./i, "");
                } catch {
                    label = url;
                }
            }

            return { url, label };
        })
        .filter(Boolean);
}

function formatUpdatedDate(value, language) {
    if (!value) return null;

    const date = new Date(String(value).replace(" ", "T"));

    if (Number.isNaN(date.getTime())) return null;

    return {
        iso: date.toISOString().slice(0, 10),
        label: new Intl.DateTimeFormat(LOCALES[language] || LOCALES.rom, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(date),
    };
}

/**
 * Profile block. `showExtras` adds what only the profile page shows: sources, last update and the correction link.
 */
function WomanProfileDetails({ woman, className = "", nameHeadingLevel = "h1", showExtras = false, children }) {
    const { language, t, localizedPath } = useTranslation();
    const NameHeading = nameHeadingLevel;
    const imageAlt = buildWomanImageAlt({
        name: woman.name,
        surname: woman.surname,
        category: woman.category_name,
        achievement: woman.achievement,
        place: woman.place_name,
    });
    const categoryPath = getWomanCategoryPath(woman);
    const placePath = getWomanPlacePath(woman);
    const categoryStyle = woman.category_color ? { backgroundColor: woman.category_color } : undefined;
    const sources = showExtras ? parseSources(woman.sources) : [];
    const updated = showExtras ? formatUpdatedDate(woman.updated_at, language) : null;

    return (
        <article className={`woman-profile-details card-common ${className}`.trim()}>
            <div className="woman-profile-details__layout">
                <div className="woman-profile-details__image-col">
                    <img src={getWomanLargeImageUrl(woman.image)} alt={imageAlt} className="woman-profile-details__photo" />
                    {woman.category_name &&
                        (categoryPath ? (
                            <Link
                                to={localizedPath(categoryPath)}
                                className="woman-profile-details__category"
                                style={categoryStyle}>
                                {woman.category_name}
                            </Link>
                        ) : (
                            <span className="woman-profile-details__category" style={categoryStyle}>
                                {woman.category_name}
                            </span>
                        ))}
                </div>

                <div className="woman-profile-details__info-col">
                    <NameHeading className="woman-profile-details__name">
                        {woman.name} {woman.surname}
                    </NameHeading>

                    {woman.achievement && (
                        <section className="woman-profile-details__section woman-profile-details__section--main">
                            <h2 className="woman-profile-details__section-title">{t("profile.mainAchievement")}</h2>
                            <p className="woman-profile-details__description">{woman.achievement}</p>
                        </section>
                    )}

                    {woman.place_name && (
                        <div className="woman-profile-details__meta">
                            <div className="woman-profile-details__meta-item">
                                <span className="woman-profile-details__meta-label">{t("profile.place")}</span>
                                {placePath ? (
                                    <Link to={localizedPath(placePath)}>{woman.place_name}</Link>
                                ) : (
                                    <span>{woman.place_name}</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {woman.description && (
                <section className="woman-profile-details__section woman-profile-details__section--about">
                    <h2 className="woman-profile-details__section-title">{t("profile.about")}</h2>
                    <p className="woman-profile-details__description">{woman.description}</p>
                </section>
            )}

            {sources.length > 0 && (
                <section className="woman-profile-details__section woman-profile-details__sources">
                    <h2 className="woman-profile-details__section-title">{t("profile.sources")}</h2>
                    <ul>
                        {sources.map((source) => (
                            <li key={source.url}>
                                <a href={source.url} target="_blank" rel="noopener nofollow noreferrer">
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {showExtras && (
                <footer className="woman-profile-details__footer">
                    {updated && (
                        <p className="woman-profile-details__updated">
                            {t("profile.updated")} <time dateTime={updated.iso}>{updated.label}</time>
                        </p>
                    )}
                    <p className="woman-profile-details__report">
                        {t("profile.reportIssue")} <Link to={localizedPath("/contact")}>{t("profile.reportIssueLink")}</Link>.
                    </p>
                </footer>
            )}

            {children}
        </article>
    );
}

export default WomanProfileDetails;
